import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { Typography } from "@lyfegen-platform/react-common";
import { Box, useTheme } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { CADataPoints } from "../models/flexible/ca-data-points";
import { Preparation } from "../models/generated/graphql";
import formatValidDate from "../utils/format-valid-date";
import { CaseIdContainerSectionController } from "./case-id-container-section-controller";
import { GetPatientEntitiesByEntityId } from "./types/GetPatientEntitiesByEntityId";
import { GetPatientEntitiesById } from "./types/GetPatientEntitiesById";

type InitialValuesType = GetPatientEntitiesByEntityId | GetPatientEntitiesById;

type FormValuesSetType = CADataPoints & {
	diagnosisDate: string | Date;
	preparations: Array<{ brandName: Preparation }>;
};

type SchemaType = { [K in keyof FormValuesSetType]: Yup.AnySchema };

type CAUniqueKey = {
	iin: string;
	disease: string;
	indication: string;
	treatments: Array<string>;
	requestReceiptDate: string | Date;
};

const mapFormValuesToUniqueKey = (values: FormValuesSetType): CAUniqueKey => {
	return {
		iin: values.patientId,
		disease: values.disease?.Icd10Code || "",
		indication: values.indication?.code || "",
		treatments:
			values.preparations
				?.map((p) => p.brandName?.BrandName)
				.filter(Boolean)
				.sort() || [],
		requestReceiptDate:
			formatValidDate(
				values.coverageStartDate ||
					values.therapyTrialStartDate ||
					values[AttributeIdentifiers.DiagnosisDate] ||
					values.requestReceiptDate
			) || "",
	};
};

function DuplicateFormBanner() {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const { errors } = useFormikContext<{ isFormUnique: boolean }>();

	if (!errors.isFormUnique) {
		return null;
	}

	return (
		<Box p={1} pl={2} marginTop={-3} bgcolor={palette.background.default} width="110%" marginLeft={-5}>
			<Typography variant="subtitle2">{t("cr-is-not-unique")}</Typography>
		</Box>
	);
}

export class RequestReceiptDateRelatedDefaultSectionController extends CaseIdContainerSectionController {
	createValidationRule(
		sectionSchema: Yup.ObjectSchema<SchemaType>,
		initialValues: InitialValuesType,
		translate: (key: string) => string
	): Yup.ObjectSchema<any> {
		if (this.context.noPatients) {
			return sectionSchema;
		}

		return sectionSchema.test("cr-must-be-unique", function (values: FormValuesSetType) {
			const currentKey: CAUniqueKey = mapFormValuesToUniqueKey(values);

			const existingKeys = initialValues.patients
				.flatMap((patient) => patient.assignedFlexEntities.map((entity) => entity.data as unknown as FormValuesSetType))
				.map(mapFormValuesToUniqueKey);

			if (existingKeys.every((key) => JSON.stringify(key) !== JSON.stringify(currentKey))) {
				return true;
			}

			return this.createError({ path: "isFormUnique", message: translate("cr-is-not-unique") });
		});
	}

	renderBeforeContent(): React.ReactElement {
		const { t } = useTranslation();
		const { palette } = useTheme();

		return this.context.noPatients ? (
			<Box display="flex" flexDirection="column" width="100%" justifyContent="space-between" alignItems="left">
				<Typography style={{ fontSize: 18, fontWeight: 600 }}>
					{t("bulk-coverage-selected-patient-info", { patientCount: this.context.noPatients })}
				</Typography>
				<Typography style={{ color: palette.tableText.main }}>{t("bulk-coverage-selected-patient-message")}</Typography>
			</Box>
		) : (
			<DuplicateFormBanner />
		);
	}
}
